import React, { createRef, useCallback, useEffect, useState } from 'react';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

import {
  FullscreenGalleryWrapper,
  List,
  Item,
  ThumbnailWrapper,
  ThumbnailList,
  ThumbnailItem,
  Thumbnail,
  CloseBtn,
} from '../../styled/FullScreenGallery/wrappers';
import { ThumbnailIcon } from '../../styled/Thumbnail/wrappers';
import ImageBox from '../../styled/ImageBox';
import { Image, Picture } from '../../styled/Image';
import { PlayIcon, TimesIcon } from '../../styled/Icon';
import { SrOnly } from '../../styled/SrOnly/wrappers';
import VideoEmbed from '../../styled/VideoEmbed';
import { useTrackGenericEvent } from '../../hooks/tracking/useTrackingEvents';

interface IFullscreenGallery {
  images: {
    src: string;
    sources: {
      srcSet: string;
      sizes?: string;
      media?: string;
      type?: string;
    }[];
  }[];
  thumbnails: {
    src: string;
    type: string;
  }[];
  videos?: string[];
  index: number;
  onClose: () => void;
}

const FullscreenGallery = ({
  images,
  index: initialIndex,
  onClose,
  thumbnails,
  videos,
}: IFullscreenGallery) => {
  const list = createRef<HTMLUListElement>();
  const [index, setIndex] = useState(initialIndex);
  const trackUpKey = useTrackGenericEvent({ event: 'fullscreenGalleryUpKey' });
  const trackDownKey = useTrackGenericEvent({ event: 'fullscreenGalleryDownKey' });
  const trackEscapeKey = useTrackGenericEvent({ event: 'fullscreenGalleryEscapeKey' });

  const scrollTo = (_index: number) => {
    if (list.current && images) {
      list.current.scrollTo({
        top: (_index / images.length) * list.current.clientHeight * images.length,
        behavior: 'smooth',
      });
    }
  };

  const scroll = useCallback((e: React.UIEvent<HTMLUListElement>) => {
    const ul = e.target as HTMLUListElement;
    setIndex(Math.round(ul.scrollTop / ul.clientHeight));
  }, []);

  const onKeyUp = useCallback(
    (e) => {
      if (e.key === 'ArrowUp') {
        scrollTo(Math.max(index - 1, 0));
        trackUpKey();
      }
      if (e.key === 'ArrowDown') {
        scrollTo(Math.min(index + 1, images.length - 1));
        trackDownKey();
      }
      if (e.key === 'Escape') {
        onClose();
        trackEscapeKey();
      }
    },
    [index, images],
  );

  useEffect(() => {
    if (initialIndex !== 0 && list.current && images) {
      list.current.scrollTo({
        top: (initialIndex / images.length) * list.current.clientHeight * images.length,
      });
    }

    if (list.current) {
      disableBodyScroll(list.current);
    }

    return () => {
      clearAllBodyScrollLocks();
    };
  }, []);

  useEffect(() => {
    window.addEventListener('keyup', onKeyUp);

    return () => {
      window.removeEventListener('keyup', onKeyUp);
    };
  }, [index]);

  return (
    <FullscreenGalleryWrapper>
      <List onScroll={scroll} ref={list}>
        {images.map((image) => (
          <Item key={image?.src} onClick={onClose}>
            <ImageBox width="100%" height="100%">
              <Picture sources={image.sources} image={image} />
            </ImageBox>
          </Item>
        ))}
        {videos?.map((video) => (
          <Item key={video} onClick={onClose}>
            <VideoEmbed id={video} />
          </Item>
        ))}
      </List>
      {thumbnails && thumbnails.length > 1 && (
        <ThumbnailWrapper>
          <ThumbnailList>
            {thumbnails.map(({ src, type }, i) => (
              <ThumbnailItem key={src}>
                <Thumbnail
                  onClick={() => scrollTo(i)}
                  title={`Visa bild ${i + 1}`}
                  className={(i === index && 'is-active') || undefined}>
                  <ImageBox width="100%" height="100%">
                    <Image src={src} />
                    {type === 'video' && (
                      <ThumbnailIcon className="small">
                        <PlayIcon className="icon" />
                      </ThumbnailIcon>
                    )}
                  </ImageBox>
                </Thumbnail>
              </ThumbnailItem>
            ))}
          </ThumbnailList>
        </ThumbnailWrapper>
      )}
      <CloseBtn onClick={onClose} title="Stäng fullskärmsgalleriet">
        <TimesIcon />
        <SrOnly>Stäng fullskärmsgalleriet</SrOnly>
      </CloseBtn>
    </FullscreenGalleryWrapper>
  );
};

export default FullscreenGallery;
